import { PALETTE_NAMES, paletteProperty } from "./indicators/palette";

// CSS custom properties set on the chart root from Sass variables (E13)
const COLOR_PROPERTIES = {
  background: "--chart-bg",
  grid: "--chart-grid",
  axisText: "--chart-axis-text",
  up: "--chart-up",
  down: "--chart-down",
  lineSeries: "--chart-line-series",
  areaTop: "--chart-area-top",
  areaBottom: "--chart-area-bottom",
  lastPriceText: "--chart-last-price-text",
  crosshair: "--chart-crosshair",
  crosshairLabel: "--chart-crosshair-label-bg",
  crosshairLabelText: "--chart-crosshair-label-text",
  highLowLabel: "--chart-high-low-label-bg",
  highLowLabelText: "--chart-high-low-label-text",
  countdownText: "--chart-countdown-text",
  countdownResolutionText: "--chart-countdown-resolution-text",
  positionEntry: "--chart-position-entry",
  positionEntryLine: "--chart-position-entry-line",
  positionEntryText: "--chart-position-entry-text",
  positionProfit: "--chart-position-profit",
  positionProfitText: "--chart-position-profit-text",
  positionLoss: "--chart-position-loss",
  positionLossText: "--chart-position-loss-text",
  positionProfitZone: "--chart-position-profit-zone",
  positionLossZone: "--chart-position-loss-zone",
  positionCardBg: "--chart-position-card-bg",
  positionCardIconBg: "--chart-position-card-icon-bg",
  positionCardCloseBg: "--chart-position-card-close-bg",
  positionCardBorder: "--chart-position-card-border",
  positionCardText: "--chart-position-card-text",
  positionPnlNeutral: "--chart-position-pnl-neutral",
  positionCloseIcon: "--chart-position-close-icon",
  positionCloseIconHover: "--chart-position-close-icon-hover",
  positionCloseHoverBg: "--chart-position-close-hover-bg",
  positionClosePressedBg: "--chart-position-close-pressed-bg",
  indicatorLevel: "--chart-indicator-level",
  paneBackground: "--chart-pane-bg",
  paneGrid: "--chart-pane-grid",
  paneSeparator: "--chart-pane-separator",
  indicatorLabelText: "--chart-indicator-label-text",
  drawingHandle: "--chart-drawing-handle",
};
const FONT_FAMILY_PROPERTY = "--chart-font-family";
const FALLBACK_FONT_FAMILY = "sans-serif";

const MAX_CHANNEL = 255;
const HEX_COLOR_LENGTH = 7;
const OPAQUE = 1;

let colorContext = null;

/**
 * Converts any CSS colour to [r, g, b, a] in 0..1 for WebGL. The browser
 * normalises the colour: a 2D context returns `#rrggbb` or `rgba(r, g, b, a)`.
 *
 * @param {string} cssColor
 * @returns {number[]}
 */
const parseCssColor = (cssColor) => {
  if (!colorContext) colorContext = document.createElement("canvas").getContext("2d");

  colorContext.fillStyle = "#000";
  colorContext.fillStyle = cssColor;

  const normalized = colorContext.fillStyle;

  if (normalized.startsWith("#") && normalized.length === HEX_COLOR_LENGTH) {
    const channels = [1, 3, 5].map(
      (start) => parseInt(normalized.slice(start, start + 2), 16) / MAX_CHANNEL,
    );

    return [...channels, OPAQUE];
  }

  const [red = 0, green = 0, blue = 0, alpha = OPAQUE] = (normalized.match(/[\d.]+/g) || []).map(
    Number,
  );

  return [red / MAX_CHANNEL, green / MAX_CHANNEL, blue / MAX_CHANNEL, alpha];
};

/**
 * @param {string} cssColor
 * @returns {{ css: string, rgba: number[] }} for the text layer and for WebGL
 */
export const toThemeColor = (cssColor) => ({ css: cssColor, rgba: parseCssColor(cssColor) });

/**
 * Reads the chart colours and font from CSS custom properties of `root`.
 * Each colour comes as a CSS string (text layer) and RGBA in 0..1 (WebGL).
 * `palette` holds the colours of indicator lines by PALETTE name.
 *
 * @param {HTMLElement} root - element inside the chart root, custom properties are inherited
 * @returns {{
 *   colors: Object<string, { css: string, rgba: number[] }>,
 *   palette: Object<string, { css: string, rgba: number[] }>,
 *   fontFamily: string,
 * }}
 */
export const readTheme = (root) => {
  const style = getComputedStyle(root);
  const readColor = (property) => toThemeColor(style.getPropertyValue(property).trim());
  const colors = {};
  const palette = {};

  Object.entries(COLOR_PROPERTIES).forEach(([name, property]) => {
    colors[name] = readColor(property);
  });
  PALETTE_NAMES.forEach((name) => {
    palette[name] = readColor(paletteProperty(name));
  });

  return {
    colors,
    palette,
    fontFamily: style.getPropertyValue(FONT_FAMILY_PROPERTY).trim() || FALLBACK_FONT_FAMILY,
  };
};
